import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { BACKEND_URL } from '../../lib/api';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Save, CheckCircle, Upload, Trash2 } from 'lucide-react';

const settingGroups = [
  { key: 'general', label: 'General', fields: ['site_name', 'currency', 'timezone'] },
  { key: 'investment', label: 'Investment', fields: ['min_investment', 'max_investment'] },
  { key: 'withdrawal', label: 'Withdrawal', fields: ['min_withdrawal', 'max_withdrawal', 'withdrawal_fee'] },
  { key: 'payment', label: 'Payment', fields: ['merchant_upi_id', 'merchant_upi_name', 'merchant_qr_image', 'bank_name', 'bank_account_number', 'bank_ifsc', 'bank_holder_name'] },
  { key: 'security', label: 'Security', fields: ['kyc_required', 'two_fa_required'] },
];

export default function AdminSettingsPage() {
  const [activeGroup, setActiveGroup] = useState('general');
  const [values, setValues] = useState<Record<string, string>>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({ queryKey: ['adminSettings'], queryFn: () => api.get('/settings').then(r => r.data) });

  const updateMutation = useMutation({
    mutationFn: (data: { key: string; value: string }[]) => api.patch('/settings/bulk', { updates: data }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminSettings'] }); setSaveSuccess(true); setTimeout(() => setSaveSuccess(false), 3000); },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/upload/payment-proof', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (res) => {
      setValues(v => ({ ...v, merchant_qr_image: res.data.url }));
    },
  });

  const getVal = (key: string) => values[key] ?? settings?.find((s: any) => s.key === key)?.value ?? '';

  const handleSave = () => {
    const group = settingGroups.find(g => g.key === activeGroup);
    if (!group) return;
    const updates = group.fields.map(f => ({ key: f, value: getVal(f) }));
    updateMutation.mutate(updates);
  };

  const handleQrUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
  };

  const activeGroupDef = settingGroups.find(g => g.key === activeGroup);

  return (
    <div className="space-y-6 animate-fade-in">
      <div><h1 className="text-2xl font-bold text-white">System Settings</h1><p className="text-sm text-slate-400">Configure platform settings</p></div>
      <div className="flex gap-2 p-1 bg-white/[0.04] rounded-lg overflow-x-auto">
        {settingGroups.map(g => (
          <button key={g.key} onClick={() => setActiveGroup(g.key)}
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${activeGroup === g.key ? 'bg-white/[0.06] text-primary-400 shadow-sm' : 'text-slate-300 hover:text-white'}`}>
            {g.label}
          </button>
        ))}
      </div>
      {saveSuccess && <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-sm text-emerald-400"><CheckCircle className="w-4 h-4" />Settings saved successfully.</div>}
      <Card>
        <CardHeader><div className="flex items-center justify-between"><h2 className="text-base font-semibold text-white">{activeGroupDef?.label} Settings</h2>
          <Button size="sm" onClick={handleSave} isLoading={updateMutation.isPending}><Save className="w-3.5 h-3.5 mr-1" />Save</Button>
        </div></CardHeader>
        <CardContent>
          <div className="space-y-4 max-w-xl">
            {activeGroupDef?.fields.map(f => {
              if (f === 'merchant_qr_image') {
                const qrVal = getVal('merchant_qr_image');
                return (
                  <div key={f}>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Merchant QR Code Image</label>
                    {qrVal && (
                      <div className="mb-3 relative inline-block">
                        <img src={qrVal.startsWith('/') ? `${BACKEND_URL}${qrVal}` : qrVal} alt="QR Code" className="w-48 h-48 object-contain rounded-lg border border-white/10 bg-white p-2" />
                        <button onClick={() => setValues(v => ({ ...v, merchant_qr_image: '' }))}
                          className="absolute -top-2 -right-2 p-1 bg-red-500/80 rounded-full hover:bg-red-500 transition-colors">
                          <Trash2 className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    )}
                    <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-white/20 rounded-lg cursor-pointer hover:border-primary-400/50 transition-colors text-sm text-slate-400 hover:text-white">
                      <Upload className="w-4 h-4" />
                      {qrVal ? 'Replace QR Image' : 'Upload QR Image'}
                      <input type="file" accept="image/*" className="hidden" onChange={handleQrUpload} />
                    </label>
                  </div>
                );
              }
              return (
                <Input key={f} label={f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} value={getVal(f)}
                  onChange={e => setValues(v => ({ ...v, [f]: e.target.value }))} />
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
